import type {
  FindOrganizationCaseRecordsRequest,
  GetCaseRecordResponse,
} from '@compass/client/types/case_record';
import { caseRecordService } from '@services/api';
import { useStore } from '@store/index';
import { useQuery } from '@tanstack/react-query';

import { CASES_QUERY_CONFIG } from '@/constants/queryConfig';

/**
 * Derive cursor-pagination info from a page of results. Mirrors
 * `computeCursorPaginationInfo` in `useCases`: `FindCaseRecordsResponse` has no
 * `next_cursor`, so "more" is inferred from a full page and the next cursor is the
 * last record's id.
 */
export const deriveCursorInfo = (
  records: GetCaseRecordResponse[] | undefined,
  limit: number,
): { records: GetCaseRecordResponse[]; hasMore: boolean; nextCursor: string | undefined } => {
  const list = records ?? [];
  const hasMore = list.length === limit;
  const nextCursor = hasMore && list.length > 0 ? list[list.length - 1]?.id : undefined;
  return { records: list, hasMore, nextCursor };
};

/**
 * Server-side org-wide case search for the Tier-2 PQC page.
 *
 * Calls `FindOrganizationCaseRecords` once per page (cursor pagination). Pass the
 * full filter set (`workflow_types`/`workflow_ids`, `states`, `assigned_user_ids`,
 * date range, `case_record_ids`, sort, `limit`, `start_after_id`) — the backend
 * scopes results to the caller's accessible programs. `organization_id` is filled
 * from the store. Disabled until the org id and a workflow scope
 * (`workflow_types` or `workflow_ids`) are present.
 */
// The org endpoint must always be workflow-scoped (PQC-only): either a workflow_type
// family or specific workflow_ids. Without it the query stays idle.
const hasWorkflowScope = (
  request: Omit<FindOrganizationCaseRecordsRequest, 'organization_id'>,
): boolean => (request.workflow_types?.length ?? 0) > 0 || (request.workflow_ids?.length ?? 0) > 0;

export const useOrgCases = (
  request: Omit<FindOrganizationCaseRecordsRequest, 'organization_id'>,
) => {
  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);
  const limit = request.limit ?? 50;
  const isEnabled = !!selectedOrganizationId && hasWorkflowScope(request);

  const query = useQuery({
    queryKey: ['compass', 'tier2', 'org-cases', selectedOrganizationId || '', request],
    queryFn: () =>
      caseRecordService.findOrganizationCaseRecords({
        organization_id: selectedOrganizationId!,
        ...request,
        limit,
      }),
    enabled: isEnabled,
    ...CASES_QUERY_CONFIG,
  });

  const { records, hasMore, nextCursor } = deriveCursorInfo(query.data?.case_records, limit);

  // Advanced (Atlas) queries — output_conditions / free-text / advanced sort —
  // return a keyset `next_page_token` instead of last-id cursoring. When present it
  // is authoritative for "has more" and is the cursor to send back via start_after_id.
  const nextPageToken = query.data?.next_page_token || undefined;

  return {
    ...query,
    cases: records,
    hasMore: nextPageToken ? true : hasMore,
    nextCursor,
    nextPageToken,
  };
};
